import React, { useEffect, useRef, useState } from "react";
import { OptionCard } from "@/components/common/OptionCard";
import { TableCheckRow } from "../table-check-row";
import { useNavigate } from "react-router";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { Button } from "@/components/common/Button";
import { Callout } from "@/components/common/Callout";
import { plural } from "@/components/common/setUtils";
import { SectionRule } from "components/common/SectionRule";
import { TextInput } from "@/components/common/TextInput";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { AppApiError } from "@/lib/api/apps/apps";
import {
  useCreateDesign,
  useProviders,
  useSchemaSourceTables,
  useRun,
  useStopApp,
} from "@/lib/api/apps/hooks";
import type { DraftTable } from "@/lib/api/apps/types";
import {
  chooseModel,
  DRAFT_POLL_MS,
  draftFailure,
  findModel,
  progressOf,
  usableProviders,
} from "./draft-model";
import { DraftProgressPanel } from "./draft-progress";
import { ModelChip, NoKeysLine, useActiveOrgRef } from "./model-field";
import { costLimitCents } from "../app-format";
import { SpaceModelPicker, useEditableModels } from "./target-picker";
import { FIVE_ROWS } from "../picker";
import {
  AppPageHead,
  AppPageShell,
  Composer,
  NEW_APP_TRAIL,
  TitleInput,
} from "../page-shell";

export type Start = "brief" | "tables";

function FieldError({ children }: { children: React.ReactNode }) {
  return (
    <span role="alert" className="text-error text-small">
      {children}
    </span>
  );
}

export function designProblem(setup: {
  name: string;
  start: Start;
  brief: string;
  tables: number;
  hasModel: boolean;
  costLimit: string;
}): string | null {
  if (!setup.name.trim()) return "Needs a name";
  if (setup.start === "brief" && !setup.brief.trim())
    return "Needs a description of the tables";
  if (setup.start === "tables" && setup.tables === 0)
    return "Needs a table to start from";
  if (!setup.hasModel) return "Needs a model to draft with";
  if (costLimitCents(setup.costLimit) === null)
    return "Needs a cost limit above $0 and up to $1,000";
  return null;
}

/** `/apps/new/design`: new tables from a description, or the tables of a
 *  model as the start. Creating it queues v1, shown live as it drafts; a
 *  draft that makes nothing takes its app with it. */
export const DesignFormPage: React.FC = () => {
  const navigate = useNavigate();
  const orgId = useOrgContextStore((s) => s.activeOrgId);
  const org = useActiveOrgRef();
  const editable = useEditableModels();
  const providers = useProviders(orgId);
  const create = useCreateDesign();
  const [made, setMade] = useState<{ app_id: string; run_id: string } | null>(
    null,
  );
  const stop = useStopApp(made?.app_id ?? null);
  const [name, setName] = useState("");
  const [start, setStart] = useState<Start>("brief");
  const [brief, setBrief] = useState("");
  const [costLimit, setCostLimit] = useState("3");
  const [source, setSource] = useState<{
    space: string | null;
    model: string | null;
  }>({ space: null, model: null });
  const [unticked, setUnticked] = useState<string[]>([]);
  // Set by a Draft click with something missing; the fields stay red until filled.
  const [tried, setTried] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const briefRef = useRef<HTMLTextAreaElement>(null);
  const [picked, setPicked] = useState<{
    provider: string;
    model: string;
  } | null>(null);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);

  const sourceTables = useSchemaSourceTables(
    orgId,
    start === "tables" ? source.model : null,
  );
  const seed: DraftTable[] =
    start === "tables"
      ? (sourceTables.data ?? []).filter((t) => !unticked.includes(t.id))
      : [];
  const run = useRun(
    made?.app_id ?? null,
    made?.run_id ?? null,
    true,
    DRAFT_POLL_MS,
  );
  const progress = run.data ? progressOf(run.data.run, run.data.events) : null;
  const ended = progress?.state === "done" || progress?.state === "failed";
  const drafting = Boolean(made) || create.isPending;
  useEffect(() => {
    if (progress?.state === "done" && made) navigate(`/apps/${made.app_id}`);
  }, [progress?.state, made, navigate]);

  const choice = providers.data ? chooseModel(providers.data, picked) : null;
  const problem = designProblem({
    name,
    start,
    brief,
    tables: seed.length,
    hasModel: Boolean(choice),
    costLimit,
  });
  const attempted =
    create.variables && providers.data
      ? findModel(
          providers.data,
          create.variables.provider,
          create.variables.model,
        )
      : null;
  const refused = create.error;
  const endedWith = progress?.error
    ? new AppApiError(0, progress.error.code, progress.error.message)
    : null;
  // A round the person stopped is no failure: the setup comes back as it was.
  const youStopped = [refused, endedWith].some(
    (e) => e instanceof AppApiError && e.code === "stopped",
  );
  const failure =
    !youStopped && (refused || endedWith)
      ? draftFailure(refused ?? endedWith, attempted ?? choice)
      : null;
  const working = drafting && !failure && !ended;
  const otherModels = providers.data
    ? usableProviders(providers.data).flatMap((p) => p.models).length > 1
    : false;

  const missing = {
    name: tried && !name.trim(),
    brief: tried && start === "brief" && !brief.trim(),
    tables: tried && start === "tables" && seed.length === 0,
    costLimit: costLimitCents(costLimit) === null,
  };
  const submit = () => {
    if (problem) {
      setTried(true);
      if (!name.trim()) nameRef.current?.focus();
      else if (start === "brief" && !brief.trim()) briefRef.current?.focus();
      return;
    }
    if (!orgId || !choice) return;
    create.mutate(
      {
        org_id: orgId,
        name,
        brief: brief.trim(),
        target_model_id: null,
        provider: choice.provider.id,
        model: choice.model.id,
        cost_limit_cents: costLimitCents(costLimit) ?? 300,
        seed_tables: seed.length ? seed : undefined,
      },
      { onSuccess: setMade },
    );
  };
  const backToSetup = () => {
    setMade(null);
    create.reset();
    stop.reset();
  };
  useEffect(() => {
    if (youStopped) backToSetup();
    // Only the stop landing matters, not the callbacks' identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youStopped]);

  const fixes = (failure?.fixes ?? []).flatMap((fix) => {
    if (fix === "key" && providers.data?.can_manage_keys && orgId)
      return [
        <Button
          key="key"
          size="sm"
          variant="secondary"
          onClick={() => navigate(`/organizations/${orgId}#models`)}
        >
          Replace the key in settings
        </Button>,
      ];
    if (fix === "another" && otherModels)
      return [
        <Button
          key="another"
          size="sm"
          variant="secondary"
          onClick={() => {
            backToSetup();
            setModelMenuOpen(true);
          }}
        >
          Pick another model
        </Button>,
      ];
    return [];
  });

  const draftingModel = attempted ?? choice;
  const sendLabel = start === "brief" ? "Draft tables" : "Draft changes";

  return (
    <AppPageShell maxWidth="46rem">
      <AppPageHead
        trail={NEW_APP_TRAIL}
        kind="Design"
        onClose={() => navigate("/apps/new")}
        title={
          <span className="flex flex-col">
            <TitleInput
              ref={nameRef}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Untitled design app"
              aria-invalid={missing.name || undefined}
              disabled={drafting}
              className="w-full"
            />
            {missing.name && <FieldError>Give the app a name</FieldError>}
          </span>
        }
      />

      {drafting ? (
        <section className="flex flex-col gap-4">
          <div
            className="flex gap-3 rounded-app border border-border bg-bg-subtle"
            style={{ padding: "var(--spacing-md) var(--spacing-lg)" }}
          >
            {start === "brief" ? (
              <EditNoteOutlinedIcon
                sx={{ fontSize: 16 }}
                className="text-fg-muted mt-0.5"
              />
            ) : (
              <TableChartOutlinedIcon
                sx={{ fontSize: 16 }}
                className="text-fg-muted mt-0.5"
              />
            )}
            <span className="flex-1 min-w-0 text-fg whitespace-pre-wrap">
              {brief.trim() ||
                `${plural(seed.length, "table")}, tightened as they stand`}
            </span>
          </div>
          <DraftProgressPanel
            progress={progress}
            model={draftingModel?.model.name ?? "The agent"}
            version={1}
            onStop={made ? () => stop.mutate() : undefined}
            stopping={stop.isPending || stop.isSuccess}
            footer={
              working ? (
                <span className="text-fg-muted-regular-small">
                  Keeps going if you leave · lands in Applications
                </span>
              ) : undefined
            }
          />
          {failure && (
            <Callout
              variant="error"
              title={failure.title}
              action={
                <span className="flex flex-wrap gap-2">
                  {fixes}
                  <Button size="sm" variant="secondary" onClick={backToSetup}>
                    Back to the setup
                  </Button>
                </span>
              }
            >
              <span className="flex flex-col gap-1">
                <span
                  className="text-mono-small"
                  style={{ overflowWrap: "anywhere" }}
                >
                  {failure.detail}
                </span>
                <span className="text-fg-muted-regular-small">
                  Nothing was created.
                </span>
              </span>
            </Callout>
          )}
        </section>
      ) : (
        <>
          <div
            role="radiogroup"
            aria-label="Start from"
            className="grid gap-3"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(16rem, 1fr))",
            }}
          >
            <OptionCard
              selected={start === "brief"}
              icon={<EditNoteOutlinedIcon sx={{ fontSize: 18 }} />}
              title="New tables"
              detail="From a description"
              onSelect={() => setStart("brief")}
            />
            <OptionCard
              selected={start === "tables"}
              icon={<TableChartOutlinedIcon sx={{ fontSize: 18 }} />}
              title="Tables I have"
              detail="Their schema as the start"
              onSelect={() => setStart("tables")}
            />
          </div>

          {start === "tables" && (
            <section className="flex flex-col gap-2">
              <SectionRule
                label="Tables"
                info="Only their shape is copied. Their rows stay in their model."
              />
              {missing.tables && (
                <FieldError>Pick the tables to start from</FieldError>
              )}
              <SpaceModelPicker
                models={editable}
                value={source}
                onChange={(next) => {
                  setSource(next);
                  setUnticked([]);
                }}
              />
              {sourceTables.isLoading && source.model && (
                <span className="text-mono-small-muted">
                  Reading the tables…
                </span>
              )}
              {sourceTables.error && (
                <Callout variant="error">
                  {(sourceTables.error as Error).message}
                </Callout>
              )}
              {sourceTables.data && sourceTables.data.length > 0 && (
                <div className="rounded-app border border-border bg-bg-subtle p-1">
                  <ul
                    role="group"
                    aria-label="Tables to start from"
                    className="flex flex-col m-0 p-0 overflow-y-auto scrollbar-overlay-hover"
                    style={{ maxHeight: FIVE_ROWS }}
                  >
                    {sourceTables.data.map((t) => {
                      const on = !unticked.includes(t.id);
                      return (
                        <li key={t.id} className="list-none">
                          <TableCheckRow
                            on={on}
                            onToggle={() =>
                              setUnticked((u) =>
                                on ? [...u, t.id] : u.filter((x) => x !== t.id),
                              )
                            }
                            name={t.name}
                            fact={plural(t.columns.length, "column")}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </section>
          )}

          <section className="flex flex-col gap-2">
            <SectionRule
              label="Cost limit"
              info="What every round of this app may spend on models, together. A round stops once it is spent."
            />
            <div className="flex items-center gap-2 text-fg-muted-regular-small">
              <span className="font-mono">$</span>
              <TextInput
                value={costLimit}
                onChange={setCostLimit}
                ariaLabel="Cost limit in US dollars"
                ariaInvalid={missing.costLimit}
                className="input-custom-sm w-24"
              />
              <span>for all rounds</span>
            </div>
            {missing.costLimit && (
              <FieldError>A limit above $0 and up to $1,000</FieldError>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <SectionRule
              label={
                start === "brief" ? "Describe the tables" : "What should change"
              }
            />
            <Composer
              ref={briefRef}
              invalid={missing.brief}
              aria-invalid={missing.brief || undefined}
              value={brief}
              onChange={(e) => {
                setBrief(e.target.value);
                create.reset();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
              }}
              aria-label={
                start === "brief" ? "Describe the tables" : "What should change"
              }
              placeholder={
                start === "brief"
                  ? "What to track, what one row is, and what you will ask of the data"
                  : "Optional: what to add, split, retype or drop"
              }
              bar={
                <>
                  {providers.data && choice ? (
                    <ModelChip
                      catalog={providers.data}
                      org={org}
                      choice={choice}
                      onPick={(provider, model) => {
                        setPicked({ provider, model });
                        create.reset();
                      }}
                      menuOpen={modelMenuOpen}
                      onMenuOpen={setModelMenuOpen}
                    />
                  ) : providers.isLoading ? (
                    <span className="text-mono-small-muted">
                      Reading the models…
                    </span>
                  ) : null}
                  <span className="flex-1" />
                  <Button variant="primary" disabled={!orgId} onClick={submit}>
                    {sendLabel}
                  </Button>
                </>
              }
            />
            {missing.brief && <FieldError>Describe the tables</FieldError>}
            {providers.data && orgId && (
              <NoKeysLine catalog={providers.data} orgId={orgId} />
            )}
            {providers.error && (
              <Callout variant="error" title="The models could not be read">
                {(providers.error as Error).message}
              </Callout>
            )}
          </section>
        </>
      )}
    </AppPageShell>
  );
};
